import React, { DependencyList, useCallback } from 'react'
import { NavigateFunction } from 'react-router-dom'

export function useLeaveGuard(dependencies: DependencyList, confirmMessage: string): [dirty: boolean, reset:() => void] {
  const [dirty, setDirty] = React.useState(false)
  const [initalState, setInitialState] = React.useState<string[]>()

  function reset() {
    if (dependencies.length > 0) {
      setInitialState(dependencies.map(d => JSON.stringify(d)))
      if (dirty) {
        // window.history.back() の実行時に onPopState が呼ばれるのを抑止するためイベントリスナーを解除する
        window.removeEventListener('popstate', onPopState, false)
        window.removeEventListener('beforeunload', onBeforeUnload, false)

        // リセットする前にダーティフラグが立っていた場合、
        // 余分に履歴がpushされているので、リセットするタイミングで１つ前に戻る。
        // （ブラウザの見た目上、forward出来そうな見た目になるが、実害は無い）
        window.history.back()
      }
      setDirty(false)
    } else {
      // 空の配列を指定された場合は常にガードする
      setDirty(true)
    }
  }

  const onPopState = useCallback(() => {
    const ok = confirm(confirmMessage)
    if (ok) {
      setDirty(false)
      window.history.back()
    } else {
      // popstateイベントが発火した時点で、最初にpushしたダミー履歴が消えている。
      // なので、キャンセルされたらもう一度pushしなおす。
      window.history.pushState(null, '', null)
    }
  }, [])

  const onBeforeUnload = useCallback((ev: BeforeUnloadEvent) => {
    ev.preventDefault()
    ev.returnValue = ''
  }, [])

  React.useEffect(() => {
    if (initalState == null) {
      reset()
    }
  }, [])

  React.useEffect(() => {
    if (dirty) {
      // popstateイベントは履歴スタックをpopした後に呼ばれるため、
      // あらかじめダミーの履歴をpushしておくことで
      // ページ遷移を回避し、実質的にブラウザバックを抑止する。
      window.history.pushState(null, '', null)
      window.addEventListener('popstate', onPopState, false)
      window.addEventListener('beforeunload', onBeforeUnload, false)
    }
    return () => {
      window.removeEventListener('popstate', onPopState, false)
      window.removeEventListener('beforeunload', onBeforeUnload, false)
    }
  }, [dirty])

  React.useEffect(() => {
    if (dirty) return
    if (initalState != null) {
      for (let i = 0; i < dependencies.length; i++) {
        if (initalState[i] !== JSON.stringify(dependencies[i])) {
          setDirty(true)
          break
        }
      }
    }
  }, dependencies)

  return [dirty, reset]
}

// TODO: useLeaveGuardへの統合
// 画面遷移時の変更破棄ダイアログの表示（画面のforward遷移時用）
export function forwardGuard(confirmMessage: string, navigate: NavigateFunction, forwardUrl: string, isChanged: boolean) {
  if (isChanged) {
    const ok = confirm(confirmMessage)
    if (ok) {
      navigate(forwardUrl)
    }
  } else {
    navigate(forwardUrl)
  }
}
